import { useMemo, useState, useCallback } from 'react';
import { QueryClient, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { StorageRead, SortDirection, FormStatus } from '@/types';
import { getStorageDocs, getStorageDoc, addStorageDoc, setStorageDoc, deleteStorageDoc, restoreStorageDoc } from '@/services/firebaseApi';
import { clearTargetStorage, getTargetStorageDocId } from '@/utils/targetStorage';
import { logger } from '@/utils/secureLogger';
import { trackCreate, trackDelete, trackEdit, trackRestore } from '@/services/analytics';
import useAuth from './useAuth';

export enum StoragesSortBy {
    NAME = 'name',
    LOCATION = 'location',
    DESCRIPTION = 'description',
}

interface UseStoragesProps {
    storageId?: string;
}

/** Drop cached storage reads so the next fetch shows current access. */
export const clearStoragesCache = (queryClient: QueryClient, storageId?: string) => {
    const refreshes = [
        queryClient.resetQueries({ queryKey: ['storages'] }),
    ];
    if (storageId) {
        refreshes.push(queryClient.resetQueries({ queryKey: ['storage', storageId] }));
    }
    return Promise.all(refreshes);
};

const useStorages = ({ storageId }: UseStoragesProps = {}) => {
    const queryClient = useQueryClient();
    
    // Filter states
    const [sortBy, setSortBy] = useState(StoragesSortBy.NAME);
    const [sortDirection, setSortDirection] = useState(SortDirection.ASCENDING);
    const [filterQuery, setFilterQuery] = useState<string>('');
    const [displayDeleted, setDisplayDeleted] = useState<boolean>(false);

    const { getUserId } = useAuth();

    // All storages query
    const {
        data: allStorages,
        isLoading: storagesLoading,
        error: storagesError,
        refetch: refetchStorages
    } = useQuery({
        queryKey: ['storages', displayDeleted],
        queryFn: () => getStorageDocs(displayDeleted),
        enabled: !!getUserId(),
        staleTime: 10 * 60 * 1000, // Storage list doesn't change often
        gcTime: 20 * 60 * 1000,
    });

    // Single storage query
    const {
        data: storage,
        isLoading: storageLoading,
        error: storageError,
        refetch: refetchStorage
    } = useQuery({
        queryKey: ['storage', storageId],
        queryFn: () => getStorageDoc(storageId!),
        enabled: !!(getUserId() && storageId),
        staleTime: 5 * 60 * 1000,
    });

    // Apply client-side filtering and sorting
    const filteredStorages = useMemo(() => {
        if (!allStorages) return [];
        
        let filtered = allStorages.filter((item) => item.instantDeletion !== true);

        // Apply search filter
        if (filterQuery) {
            const lower = filterQuery.toLowerCase();
            filtered = filtered.filter((storage) =>
                storage.name.toLowerCase().includes(lower) ||
                (storage.description && storage.description.toLowerCase().includes(lower)) ||
                (storage.location && storage.location.toLowerCase().includes(lower))
            );
        }

        // Note: displayDeleted filtering is now handled server-side via the includeDeleted parameter

        // Apply sorting
        filtered.sort((a, b) => {
            const aValue = a[sortBy as keyof StorageRead] || '';
            const bValue = b[sortBy as keyof StorageRead] || '';
            
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                const comparison = aValue.localeCompare(bValue);
                return sortDirection === SortDirection.ASCENDING ? comparison : -comparison;
            }
            
            return 0;
        });

        return filtered;
    }, [allStorages, filterQuery, sortBy, sortDirection]);

    // Mutations
    const createStorageMutation = useMutation({
        mutationFn: addStorageDoc,
        onSuccess: (results) => {
            trackCreate('storage');
            logger.info('Storage created successfully');
            // Invalidate both displayDeleted states
            queryClient.invalidateQueries({ queryKey: ['storages', true] });
            queryClient.invalidateQueries({ queryKey: ['storages', false] });
            queryClient.invalidateQueries({ queryKey: ['accountPublicData'] });
            // Optionally return the new storageId
            //return results.storageId;
        },
        onError: (error) => {
            logger.error('Failed to create storage:', error);
        }
    });

    const updateStorageMutation = useMutation({
        mutationFn: setStorageDoc,
        onSuccess: (updatedStorage, variables) => {
            trackEdit('storage');
            logger.info('Storage updated successfully:', variables.id);
            // Invalidate all storage caches
            queryClient.invalidateQueries({ queryKey: ['storage', variables.id] });
            queryClient.invalidateQueries({ queryKey: ['storages', true] });
            queryClient.invalidateQueries({ queryKey: ['storages', false] });
        },
        onError: (error) => {
            logger.error('Failed to update storage:', error);
        }
    });

    const deleteStorageMutation = useMutation({
        mutationFn: deleteStorageDoc,
        onSuccess: (_deleteResult, { storageId, instantDeletion }) => {
            trackDelete('storage');
            logger.info('Storage deleted successfully:', storageId);

            const dropStorage = (current: StorageRead[] | undefined) =>
                current?.filter((item) => item.id !== storageId)

            queryClient.removeQueries({ queryKey: ['storage', storageId] });
            queryClient.setQueryData<StorageRead[]>(['storages', false], dropStorage);
            if (instantDeletion) {
                queryClient.setQueryData<StorageRead[]>(['storages', true], dropStorage);
            }
            if (getTargetStorageDocId() === storageId) clearTargetStorage();

            queryClient.invalidateQueries({ queryKey: ['storages', true] });
            queryClient.invalidateQueries({ queryKey: ['storages', false] });
            queryClient.invalidateQueries({ queryKey: ['accountPublicData'] });
        },
        onError: (error) => {
            logger.error('Failed to delete storage:', error);
        }
    });

    const restoreStorageMutation = useMutation({
        mutationFn: restoreStorageDoc,
        onSuccess: (result, storageId) => {
            trackRestore('storage');
            logger.info('Storage restored successfully:', storageId);
            
            // Invalidate both displayDeleted states
            queryClient.invalidateQueries({ queryKey: ['storages', true] });
            queryClient.invalidateQueries({ queryKey: ['storages', false] });
            queryClient.invalidateQueries({ queryKey: ['storage', storageId] });
            queryClient.invalidateQueries({ queryKey: ['accountPublicData'] });
        },
        onError: (error) => {
            logger.error('Failed to restore storage:', error);
        }
    });

    // Helper function to determine status
    const getStatus = (loading: boolean, error: any): FormStatus => {
        if (loading) return FormStatus.LOADING;
        if (error) return FormStatus.ERROR;
        return FormStatus.SUCCESS;
    };

    // Backward compatible functions
    const reloadStorages = () => {
        if (storageId) {
            refetchStorage();
        } else {
            refetchStorages();
        }
    };

    const storageIsAccessible = (targetStorageId: string) => {
        if (storageId) {
            return storage !== null;
        } else {
            return allStorages?.some((item: StorageRead) => item.id === targetStorageId && item.instantDeletion !== true) || false;
        }
    };

    // Computed values
    const lastAddedSku = useMemo(() => {
        if (!storageId || !storage) return 1;
        
        const counter = storage.lastAdded?.sku;
        if (!counter) return 1;
        
        if (typeof counter === 'number') {
            return counter + 1;
        }
        
        if (typeof counter === 'string') {
            // Check if string ends with numbers
            const match = counter.match(/^(.*?)(\d+)$/);
            if (match) {
                const prefix = match[1];
                const number = parseInt(match[2], 10);
                return `${prefix}${number + 1}`;
            } else {
                // String doesn't end with numbers, append 1
                return `${counter}1`;
            }
        }
        
        return 1;
    }, [storage, storageId]);

    const lastAddedCategoryId = useMemo(() => {
        if (!storageId || !storage) return 0;
        return (storage.lastAdded?.categoryId || 0);
    }, [storage, storageId]);

    const lastAddedTransactionId = useMemo(() => {
        if (!storageId || !storage) return 0;
        return (storage.lastAdded?.transactionId || 0);
    }, [storage, storageId]);

    const lastAddedCustomerId = useMemo(() => {
        if (!storageId || !storage) return 0;
        return (storage.lastAdded?.customerId || 0);
    }, [storage, storageId]);

    const lastAddedSupplierId = useMemo(() => {
        if (!storageId || !storage) return 0;
        return (storage.lastAdded?.supplierId || 0);
    }, [storage, storageId]);

    // Helper functions to check if features are enabled
    const isTransactionsEnabled = useCallback(() => {
        if (!storage) return true; // Default to true for backwards compatibility
        return storage.settings?.enableTransactions !== false;
    }, [storage]);

    const isActionsRequiredToBeLinkedToTransactions = useCallback(() => {
        if (!storage) return false;
        return storage.settings?.requireActionsLinkedToTransactions === true;
    }, [storage]);

    const isStockAdjustmentEnabled = useCallback(() => {
        if (!storage) return true;
        return storage.settings?.enableStockAdjustment !== false;
    }, [storage]);

    const isCustomersAndSuppliersEnabled = useCallback(() => {
        if (!storage) return false;
        // Opt-in: match storage settings, which treat unset as disabled
        if (storage.settings?.enableCustomersAndSuppliers !== undefined) {
            return storage.settings.enableCustomersAndSuppliers === true;
        }
        // Fallback for old data structure (before unified setting)
        return (storage as any).enableCustomers === true;
    }, [storage]);

    const isSharedAccessEnabled = useCallback(() => {
        if (!storage) return true; // Default to true for backwards compatibility
        const storagePlan = storage.settings?.storagePlan;
        // Shared access is only available for pro and premium plans
        return storagePlan === 'pro' || storagePlan === 'premium';
    }, [storage]);

    // Backward compatibility aliases
    const isCustomersEnabled = isCustomersAndSuppliersEnabled;
    const isSuppliersEnabled = isCustomersAndSuppliersEnabled;

    return {
        // Single storage
        storage: storage || null,
        storageStatus: getStatus(storageLoading, storageError),
        storageIsAccessible,
        
        // Multiple storages
        storages: (allStorages || []).filter((item) => item.instantDeletion !== true),
        filteredStorages,
        storagesStatus: getStatus(storagesLoading, storagesError),
        
        // Filtering and sorting
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        filterQuery,
        setFilterQuery,
        displayDeleted,
        setDisplayDeleted,
        
        // Actions
        reloadStorages,
        
        // Mutations (async only)
        createStorageAsync: createStorageMutation.mutateAsync,
        updateStorageAsync: updateStorageMutation.mutateAsync,
        deleteStorageAsync: deleteStorageMutation.mutateAsync,
        restoreStorageAsync: restoreStorageMutation.mutateAsync,
        isCreating: createStorageMutation.isPending,
        isUpdating: updateStorageMutation.isPending,
        isDeleting: deleteStorageMutation.isPending,
        isRestoring: restoreStorageMutation.isPending,
        
        // Computed values
        lastAddedSku,
        lastAddedCategoryId,
        lastAddedTransactionId,
        lastAddedCustomerId,
        lastAddedSupplierId,
        
        // Feature toggles
        isTransactionsEnabled,
        isActionsRequiredToBeLinkedToTransactions,
        isStockAdjustmentEnabled,
        isCustomersAndSuppliersEnabled,
        isCustomersEnabled, // Backward compatibility alias
        isSuppliersEnabled, // Backward compatibility alias
        isSharedAccessEnabled,
        
        // Refresh functions
        refreshStorages: refetchStorages,
        refreshStorage: refetchStorage,
    };
};

export default useStorages;
